import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../stateManagers/apiSetup/axiosInterceptor';

const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  gold: '#fbb21f',
  white: '#ffffff',
  border: '#e5e7eb',
  sub: '#6b7280',
  muted: '#9ca3af',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

interface BatchListItem {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id?: string;
  course_name?: string;
  course_title?: string;
  is_active: boolean;
}

type BatchStatusFilter = 'active' | 'inactive' | 'all';

interface BatchFilterControlProps {
  selectedBatchId: string | null;
  onBatchChange: (batchId: string | null) => void;
  /** Optional DOM node to portal the panel into. Pass a full-width slot
   *  rendered as a direct sibling of the toolbar row (not nested inside a
   *  shrink-to-fit flex cluster) so the panel gets genuine 100% width and
   *  doesn't wrap awkwardly inside the button group. If omitted, falls
   *  back to rendering inline (old behavior). */
  panelContainer?: HTMLElement | null;
}

const PAGE_SIZE = 24;
const SCROLL_STEP = 260;

// Hides the native scrollbar on the batch strip while keeping it scrollable
// (mouse wheel, touch, and the arrow buttons still work).
const scrollbarHideStyles = `
  .batch-strip-no-scrollbar {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }
  .batch-strip-no-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Edge Chromium */
  }
`;

const BatchFilterControl: React.FC<BatchFilterControlProps> = ({ selectedBatchId, onBatchChange, panelContainer }) => {
  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [batchStatusFilter, setBatchStatusFilter] = useState<BatchStatusFilter>('active');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isBatchFiltered = !!selectedBatchId;

  const isActiveParam: boolean | undefined =
    batchStatusFilter === 'active' ? true : batchStatusFilter === 'inactive' ? false : undefined;

  const fetchBatches = async (pageToLoad: number, append: boolean) => {
    try {
      if (append) setLoadingMore(true);
      else setBatchesLoading(true);
      setBatchesError(null);
      const res = await apiClient.get('/v1/batches', {
        params: {
          page: pageToLoad,
          limit: PAGE_SIZE,
          ...(isActiveParam !== undefined ? { is_active: isActiveParam } : {}),
        },
      });
      if (res.data.success === false) {
        setBatchesError(res.data.error || 'Failed to load batches');
        return;
      }
      const rows = res.data?.data ?? [];
      const mapped: BatchListItem[] = rows.map((b: any) => ({
        batch_id: b.batch_id,
        batch_name: b.batch_name,
        batch_time: b.batch_time,
        course_id: b.course_id,
        course_name: b.course_name,
        course_title: b.course_title,
        is_active: !!b.is_active,
      }));
      setBatches((prev) => (append ? [...prev, ...mapped] : mapped));
      const totalPages = res.data?.pagination?.pages ?? 0;
      setPage(pageToLoad);
      setHasMore(pageToLoad < totalPages);
    } catch (err: any) {
      setBatchesError(err.response?.data?.error || 'Failed to load batches');
    } finally {
      setBatchesLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setHasMore(true);
    fetchBatches(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchStatusFilter]);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    // Recompute arrow visibility whenever the batch list changes (initial load, filter change, pagination).
    updateScrollButtons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batches]);

  const handleSelectBatch = (batchId: string) => {
    onBatchChange(selectedBatchId === batchId ? null : batchId);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    updateScrollButtons();
    if (loadingMore || batchesLoading || !hasMore) return;
    const el = e.currentTarget;
    if (el.scrollWidth - el.scrollLeft - el.clientWidth < 120) {
      fetchBatches(page + 1, true);
    }
  };

  const scrollByStep = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP, behavior: 'smooth' });
    // scrollBy is async/smooth, so re-check button state shortly after.
    window.setTimeout(updateScrollButtons, 300);
  };

  const arrowButtonStyle = (disabled: boolean): React.CSSProperties => ({
    flexShrink: 0,
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: `1px solid ${C.border}`,
    background: C.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.35 : 1,
    boxShadow: '0 1px 3px rgba(14,34,93,0.1)',
    color: C.navy,
    transition: 'opacity 0.15s ease',
  });

  const panel = (
    <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <style>{scrollbarHideStyles}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.sub }}>Batch status</span>
          <div style={{ display: 'flex', background: C.white, border: `1px solid ${C.border}`, borderRadius: '8px', overflow: 'hidden' }}>
            {(['active', 'inactive', 'all'] as BatchStatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setBatchStatusFilter(s)}
                style={{
                  padding: '6px 14px',
                  border: 'none',
                  borderRight: s !== 'all' ? `1px solid ${C.border}` : 'none',
                  background: batchStatusFilter === s ? C.navy : 'transparent',
                  color: batchStatusFilter === s ? C.gold : C.sub,
                  fontSize: '0.76rem',
                  fontWeight: '700',
                  textTransform: 'capitalize',
                  cursor: 'pointer',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {isBatchFiltered && (
          <button
            onClick={() => onBatchChange(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: C.redDim,
              border: `1px solid ${C.redBorder}`,
              borderRadius: '8px',
              color: C.red,
              fontSize: '0.76rem',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.68rem' }} />
            Clear batch filter
          </button>
        )}
      </div>

      {batchesError && <Message severity="error" text={batchesError} style={{ width: '100%', fontSize: '0.85rem' }} />}

      {batchesLoading && batches.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px' }}>
          <ProgressSpinner style={{ width: '22px', height: '22px' }} strokeWidth="4" />
          <span style={{ fontSize: '0.82rem', color: C.sub }}>Loading batches…</span>
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: C.white,
            borderRadius: '14px',
            padding: '14px 18px',
            boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
            border: `1px solid ${C.border}`,
            opacity: batchesLoading ? 0.55 : 1,
            transition: 'opacity 0.2s ease',
            pointerEvents: batchesLoading ? 'none' : 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              aria-label="Scroll batches left"
              onClick={() => scrollByStep('left')}
              disabled={!canScrollLeft}
              style={arrowButtonStyle(!canScrollLeft)}
            >
              <i className="pi pi-chevron-left" style={{ fontSize: '0.75rem' }} />
            </button>

            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="batch-strip-no-scrollbar"
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                overflowY: 'hidden',
                gap: '8px',
                paddingBottom: '6px',
                scrollSnapType: 'x proximity',
                WebkitOverflowScrolling: 'touch',
                flex: 1,
                minWidth: 0,
              }}
            >
              {batches.map((batch) => {
                const isSelected = selectedBatchId === batch.batch_id;
                return (
                  <div
                    key={batch.batch_id}
                    onClick={() => handleSelectBatch(batch.batch_id)}
                    title={batch.batch_name}
                    style={{
                      padding: '8px 12px',
                      background: isSelected ? `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)` : C.white,
                      border: isSelected ? '1.5px solid transparent' : `1.5px solid ${C.border}`,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, transform 0.15s ease',
                      transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: '0 0 220px',
                      scrollSnapAlign: 'start',
                      boxShadow: isSelected ? '0 3px 10px rgba(14,34,93,0.25)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: isSelected ? C.gold : 'rgba(14,34,93,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'background 0.25s ease',
                      }}
                    >
                      <i className="pi pi-users" style={{ fontSize: '0.62rem', color: C.navy }} />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: '600',
                          color: isSelected ? 'white' : C.navy,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          transition: 'color 0.25s ease',
                        }}
                      >
                        {batch.batch_name}
                      </span>
                      <span style={{ fontSize: '0.58rem', color: isSelected ? 'rgba(255,255,255,0.65)' : '#616366', transition: 'color 0.25s ease' }}>
                        {batch.batch_time}
                      </span>
                    </div>

                    {isSelected && <i className="pi pi-check" style={{ fontSize: '0.68rem', color: C.gold, flexShrink: 0 }} />}
                  </div>
                );
              })}

              {batches.length === 0 && (
                <div style={{ padding: '40px 20px', textAlign: 'center', flex: '1 0 100%' }}>
                  <p style={{ margin: 0, color: C.muted, fontSize: '0.9rem' }}>No {batchStatusFilter !== 'all' ? batchStatusFilter : ''} batches found</p>
                </div>
              )}
            </div>

            <button
              type="button"
              aria-label="Scroll batches right"
              onClick={() => scrollByStep('right')}
              disabled={!canScrollRight}
              style={arrowButtonStyle(!canScrollRight)}
            >
              <i className="pi pi-chevron-right" style={{ fontSize: '0.75rem' }} />
            </button>
          </div>

          {loadingMore && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 0 0' }}>
              <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="5" />
              <span style={{ fontSize: '0.76rem', color: C.sub }}>Loading more…</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return panelContainer ? ReactDOM.createPortal(panel, panelContainer) : panel;
};

export default BatchFilterControl;